import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Scissors, User, Lock, ArrowRight, Delete } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, currentUser, setCurrentBranch } = useApp();
  const [selectedBranch, setSelectedBranch] = useState('smichov');
  const [staff, setStaff] = useState({ smichov: [], stodulky: [] });
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/pos');
    }
  }, [currentUser, navigate]);

  // Fetch staff data
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const [smichovRes, stodulkyRes] = await Promise.all([
          axios.get(`${API}/staff/smichov`),
          axios.get(`${API}/staff/stodulky`)
        ]);
        setStaff({
          smichov: smichovRes.data,
          stodulky: stodulkyRes.data
        });
      } catch (err) {
        console.error('Failed to fetch staff:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStaff();
  }, []);

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    setPin('');
    setError('');
    setPinDialogOpen(true);
  };

  const handlePinInput = React.useCallback((digit) => {
    setPin(prev => (prev.length < 4 ? prev + digit : prev));
  }, []);

  const handlePinDelete = React.useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handlePinClear = React.useCallback(() => {
    setPin('');
    setError('');
  }, []);

  const handleLogin = useCallback(async () => {
    if (pin.length !== 4 || !selectedStaff) return;
    
    setError('');
    const result = await login(selectedStaff.id, pin);
    
    if (result.success) {
      setPinDialogOpen(false);
      navigate('/pos');
    } else {
      setError(result.message || 'Nesprávný PIN');
      setPin('');
    }
  }, [pin, selectedStaff, login, navigate]);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4) {
      handleLogin();
    }
  }, [pin, handleLogin]);

  // Allow keyboard input for PIN (digits, Backspace, Enter)
  useEffect(() => {
    if (!pinDialogOpen) return;
    const onKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        handlePinInput(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handlePinDelete();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // submit if 4 digits or attempt anyway
        if (pin.length === 4) {
          handleLogin();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pinDialogOpen, pin, handleLogin, handlePinInput, handlePinDelete]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div 
      className="min-h-screen bg-obsidian flex flex-col items-center justify-center p-4 noise-overlay"
      style={{
        backgroundImage: 'linear-gradient(rgba(5,5,5,0.92), rgba(5,5,5,0.95)), url(https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Logo */}
      <div className="text-center mb-12" data-testid="login-header">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Scissors className="w-10 h-10 text-gold" strokeWidth={1.5} />
          <h1 className="font-display text-4xl sm:text-5xl font-bold gold-gradient-text tracking-tight">
            GOLD BARBERSHOP
          </h1>
        </div>
        <p className="text-muted-foreground text-lg tracking-widest uppercase">
          Pokladní Systém
        </p>
      </div>

      {/* Branch Tabs */}
      <div className="w-full max-w-4xl" data-testid="branch-selector">
        <Tabs 
          defaultValue="smichov" 
          value={selectedBranch}
          onValueChange={(value) => {
            setSelectedBranch(value);
            setCurrentBranch(value);
          }}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 bg-paper border border-white/10 h-14 mb-8">
            <TabsTrigger 
              value="smichov" 
              className="data-[state=active]:bg-gold data-[state=active]:text-black font-semibold text-lg tracking-wide"
              data-testid="tab-smichov"
            >
              Smíchov
            </TabsTrigger>
            <TabsTrigger 
              value="stodulky" 
              className="data-[state=active]:bg-gold data-[state=active]:text-black font-semibold text-lg tracking-wide"
              data-testid="tab-stodulky"
            >
              Stodůlky
            </TabsTrigger>
          </TabsList>

          {/* Staff Grid */}
          {['smichov', 'stodulky'].map((branch) => (
            <TabsContent key={branch} value={branch} className="mt-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {isLoading ? (
                  Array(4).fill(0).map((_, i) => (
                    <div 
                      key={i}
                      className="bg-paper border border-white/5 rounded-lg p-6 animate-pulse"
                    >
                      <div className="w-20 h-20 mx-auto rounded-full bg-subtle mb-4" />
                      <div className="h-4 bg-subtle rounded w-2/3 mx-auto" />
                    </div>
                  ))
                ) : (
                  staff[branch].map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleStaffSelect(member)}
                      className="bg-paper border border-white/5 hover:border-gold/50 rounded-lg p-6 transition-all duration-300 hover:gold-glow-sm group"
                      data-testid={`staff-${member.id}`}
                    >
                      <div className="relative">
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="w-20 h-20 mx-auto rounded-full object-cover border-2 border-transparent group-hover:border-gold transition-all duration-300"
                        />
                      </div>
                      <p className="mt-4 text-lg font-semibold text-center group-hover:text-gold transition-colors">
                        {member.name}
                      </p>
                      <p className="text-sm text-muted-foreground text-center capitalize">
                        Holič
                      </p>
                    </button>
                  ))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent 
          className="bg-paper/95 backdrop-blur-xl border border-gold/30 shadow-2xl max-w-sm"
          data-testid="pin-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex flex-col items-center gap-4">
                {selectedStaff && (
                  <img
                    src={selectedStaff.avatar}
                    alt={selectedStaff.name}
                    className="w-16 h-16 rounded-full border-2 border-gold"
                  />
                )}
                <span className="text-xl font-semibold">
                  {selectedStaff?.name}
                </span>
                <span className="text-muted-foreground text-sm font-normal">
                  Zadejte 4-místný PIN
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* PIN Display */}
          <div className="flex justify-center gap-3 my-6" data-testid="pin-display">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-14 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-mono transition-all duration-200 ${
                  pin.length > i 
                    ? 'border-gold bg-gold/10 text-gold' 
                    : 'border-white/20 bg-subtle'
                }`}
              >
                {pin.length > i ? '•' : ''}
              </div>
            ))}
          </div>

          {/* Error message */}
          {error && (
            <p className="text-destructive text-center text-sm mb-4" data-testid="pin-error">
              {error}
            </p>
          )}

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2" data-testid="pin-numpad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
              <Button
                key={digit}
                variant="outline"
                className="h-14 text-xl font-mono border-white/10 hover:border-gold hover:bg-gold/10 hover:text-gold"
                onClick={() => handlePinInput(digit.toString())}
                data-testid={`pin-key-${digit}`}
              >
                {digit}
              </Button>
            ))}
            <Button
              variant="outline"
              className="h-14 text-xl border-white/10 hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handlePinClear}
              data-testid="pin-key-clear"
            >
              C
            </Button>
            <Button
              variant="outline"
              className="h-14 text-xl font-mono border-white/10 hover:border-gold hover:bg-gold/10 hover:text-gold"
              onClick={() => handlePinInput('0')}
              data-testid="pin-key-0"
            >
              0
            </Button>
            <Button
              variant="outline"
              className="h-14 border-white/10 hover:border-gold hover:bg-gold/10 hover:text-gold"
              onClick={handlePinDelete}
              data-testid="pin-key-delete"
            >
              <Delete className="w-5 h-5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <p className="mt-12 text-muted-foreground text-sm">
        © {new Date().getFullYear()} Gold Barbershop. Všechna práva vyhrazena.
      </p>
    </div>
  );
};

export default LoginPage;
